"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Linkedin, Twitter, MapPin, Phone, Mail, ArrowRight } from "lucide-react";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-5 w-24 bg-muted/20 animate-pulse rounded" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return null;
  }
  return <nav className="flex flex-col gap-3">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="group flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm" data-index={i}>
          <span className="h-px w-0 bg-primary transition-all duration-300 group-hover:w-3" />
          {item.label}
        </Link>)}
    </nav>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  return <footer id="footer" className="dark bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-[var(--primary)] via-[var(--primary)]/20 to-transparent opacity-70" />

      <div className="container mx-auto px-6 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16">
          
          {}
          <div className="md:col-span-4 flex flex-col gap-6">
            <Link to="/" className="block w-fit">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1558991/img/inara_technologies_logo.png" alt="Inara Technologies" className="h-7 w-auto object-contain" />
            </Link>
            <p className="text-muted-foreground leading-relaxed text-sm md:text-base max-w-md">Harnessing IT consulting and Cloud Computing for Sustainable Growth in Kenya and East Africa. We help enterprises and startups optimize infrastructure and drive innovation.</p>
            
            <div className="pt-4">
              <Button variant="outline" className="border-primary/30 text-primary hover:bg-primary/10 hover:text-primary hover:border-primary transition-all group" asChild>
                <Link to="/ConnectPage">
                  Start Your Transformation
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="md:col-span-4 flex flex-col gap-8 md:pl-8 lg:pl-12">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_8px_var(--primary)]" />
                Explore
              </h3>
              <MenuProvider menu_id="46" className="w-full">
                <FooterMenuConsumer />
              </MenuProvider>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wider opacity-80">
                Connect
              </h3>
              <div className="flex gap-4">
                <Link to="https://linkedin.com" newTab className="group flex items-center justify-center size-10 rounded-full border border-border bg-card hover:border-primary/50 hover:bg-primary/10 hover:shadow-[0_0_15px_-3px_var(--primary)] transition-all duration-300" aria-label="LinkedIn">
                  <Linkedin className="size-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </Link>
                <Link to="https://twitter.com" newTab className="group flex items-center justify-center size-10 rounded-full border border-border bg-card hover:border-primary/50 hover:bg-primary/10 hover:shadow-[0_0_15px_-3px_var(--primary)] transition-all duration-300" aria-label="Twitter">
                  <Twitter className="size-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </Link>
              </div>
            </div>
          </div>

          {}
          <div className="md:col-span-4 flex flex-col gap-6">
            <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_8px_var(--primary)]" />
              Nairobi HQ
            </h3>
            
            <ul className="space-y-5">
              <li className="flex items-start gap-4 group">
                <div className="mt-1 flex items-center justify-center size-8 rounded bg-primary/10 text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                  <MapPin className="size-4" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">Address</span>
                  <span className="text-foreground text-sm">Nairobi, Kenya</span>
                </div>
              </li>

              <li className="flex items-start gap-4 group">
                <div className="mt-1 flex items-center justify-center size-8 rounded bg-primary/10 text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                  <Phone className="size-4" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">Phone</span>
                  <Link to="tel:+254712345678" className="text-foreground text-sm hover:text-primary transition-colors">+254 722 342 819</Link>
                </div>
              </li>

              <li className="flex items-start gap-4 group">
                <div className="mt-1 flex items-center justify-center size-8 rounded bg-primary/10 text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                  <Mail className="size-4" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">Email</span>
                  <Link to="mailto:contact@inara.tech" className="text-foreground text-sm hover:text-primary transition-colors">
                    contact@inara.tech
                  </Link>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="my-12 bg-border/50" />

        {}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="inline-block size-2 rounded-full bg-primary animate-pulse" />
            <span className="font-medium text-foreground/80">Built in Nairobi for the Region</span>
          </div>
          
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
            <p>&copy; {currentYear} Inara Technologies. All rights reserved.</p>
            <div className="flex gap-6 text-xs">
              <Link to="#" className="hover:text-primary transition-colors">Privacy Policy</Link>
              <Link to="#" className="hover:text-primary transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>;
}